import AuthService from './AuthService.js';

/**
 * Service de navigation intelligente entre les jeux
 * Sélectionne automatiquement le prochain jeu en fonction de la progression
 */
class GameNavigationService {
  constructor() {
    // Liste des jeux avec leurs routes et endpoints API
    this.games = [
      { route: '/jeune/roue-des-competences', endpoint: '/games/skills', name: 'Roue des compétences' },
      { route: '/jeune/scenarios', endpoint: '/games/scenario', name: 'Scénarios' },
      { route: '/jeune/environment', endpoint: '/games/room-env', name: 'Environnement idéal' },
      { route: '/jeune/metiers', endpoint: '/games/jobs', name: 'Exploration métiers' },
      { route: '/jeune/shape-sequence-game', endpoint: '/games/shape-sequence', name: 'Séquence de formes' },
      { route: '/jeune/game-speed', endpoint: '/games/speed', name: 'Jeu de rapidité' },
    ];
  }

  /**
   * Récupère la progression de tous les jeux
   * @returns {Promise<Object>} Objet avec la progression de chaque jeu
   */
  async fetchGamesProgress() {
    const gamesProgress = {};

    for (const game of this.games) {
      try {
        const response = await AuthService.fetchWithAuth({
          method: 'get',
          url: game.endpoint,
        });
        gamesProgress[game.endpoint] = response.data.completion || 0;
      } catch (error) {
        // Jeu non commencé, contribution de 0
        gamesProgress[game.endpoint] = 0;
      }
    }

    return gamesProgress;
  }

  /**
   * Sélectionne intelligemment le prochain jeu à jouer
   * Logique:
   * 1. Prioriser les jeux non commencés (completion = 0)
   * 2. Ensuite les jeux en cours (0 < completion < 1)
   * 3. Si tous terminés, choisir au hasard
   *
   * @param {Object} gamesProgress - Objet optionnel avec la progression déjà chargée
   * @param {string} currentGameEndpoint - Endpoint du jeu actuel pour l'exclure
   * @returns {Promise<string>} Route du prochain jeu
   */
  async getNextGame(gamesProgress = null, currentGameEndpoint = null) {
    // Si la progression n'est pas fournie, la récupérer
    if (!gamesProgress) {
      gamesProgress = await this.fetchGamesProgress();
    }

    // Ajouter la progression à chaque jeu
    const gamesWithProgress = this.games.map(game => ({
      ...game,
      completion: gamesProgress[game.endpoint] || 0,
    }));

    // Filtrer le jeu actuel si spécifié
    const availableGames = currentGameEndpoint
      ? gamesWithProgress.filter(g => g.endpoint !== currentGameEndpoint)
      : gamesWithProgress;

    // 1. Prioriser les jeux non commencés (completion = 0)
    const notStarted = availableGames.filter(g => g.completion === 0);
    if (notStarted.length > 0) {
      const randomIndex = Math.floor(Math.random() * notStarted.length);
      return notStarted[randomIndex].route;
    }

    // 2. Ensuite les jeux en cours (0 < completion < 1)
    const inProgress = availableGames.filter(g => g.completion > 0 && g.completion < 1);
    if (inProgress.length > 0) {
      // Trier par progression décroissante (les plus avancés d'abord)
      inProgress.sort((a, b) => b.completion - a.completion);
      return inProgress[0].route;
    }

    // 3. Si tous les jeux sont terminés, choisir au hasard
    const randomIndex = Math.floor(Math.random() * availableGames.length);
    return availableGames[randomIndex].route;
  }

  /**
   * Navigue vers le prochain jeu
   * @param {Object} router - Instance du router Vue
   * @param {string} currentGameEndpoint - Endpoint du jeu actuel pour l'exclure
   */
  async navigateToNextGame(router, currentGameEndpoint = null) {
    try {
      const nextGameRoute = await this.getNextGame(null, currentGameEndpoint);

      // Haptic feedback si disponible
      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50);
      }

      router.push(nextGameRoute);
    } catch (error) {
      console.error('Erreur lors de la navigation vers le prochain jeu:', error);
      // En cas d'erreur, rediriger vers le dashboard
      router.push('/jeune/dashboard');
    }
  }

  /**
   * Retourne au dashboard
   * @param {Object} router - Instance du router Vue
   */
  returnToDashboard(router) {
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(50);
    }
    router.push('/jeune/dashboard');
  }
}

export default new GameNavigationService();
